import type { QueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import { broadcastLaborOpenSessionChanged } from "@/lib/labor/laborOpenSessionSync";
import { HUB_MENU_SHELL_LABOR_PAGE } from "@/lib/labor/laborPageKey";
import type { LaborDomain } from "@/lib/labor/laborTarget";

export { HUB_MENU_SHELL_LABOR_PAGE, hubLaborExpectedPageFromPathname } from "@/lib/labor/laborPageKey";

/**
 * Domain for `GET /api/labor/active-session` must match job workspaces (`fetchLaborActiveSession`) so
 * hub mock-up write-block checks and read-only snapshots use the same policy as the page that opened the session.
 */
export function hubLaborActiveSessionDomainFromPathname(pathname: string): LaborDomain | undefined {
  if (pathname.startsWith("/clamp-qc")) {
    return "clampqc";
  }
  if (pathname.startsWith("/shipping/pre-pack")) {
    return "shipping";
  }
  if (pathname.startsWith("/manufacturing/final-qc") || pathname.startsWith("/manufacturing/pre-encapsulation")) {
    return "manufacturing";
  }
  if (pathname.startsWith("/shipping")) {
    return "shipping";
  }
  if (pathname.startsWith("/manufacturing")) {
    return "manufacturing";
  }
  return undefined;
}

export function hubLaborActiveSessionQueryKey(
  expectedPage: string,
  domain?: LaborDomain,
): readonly string[] {
  return domain !== undefined
    ? ["hub", "labor", "active-session", expectedPage, domain]
    : ["hub", "labor", "active-session", expectedPage];
}

export async function fetchHubLaborActiveSession(
  expectedPage: string,
  domain?: LaborDomain,
): Promise<Record<string, unknown>> {
  const page = expectedPage.trim() !== "" ? expectedPage.trim() : HUB_MENU_SHELL_LABOR_PAGE;
  return apiFetch<Record<string, unknown>>("/api/labor/active-session", {
    query:
      domain !== undefined
        ? {
            page,
            domain,
          }
        : { page },
  });
}

/** Invalidate the account menu labor poll after any Integrator clock transition. */
export function invalidateIntegratorLaborMenuQueries(queryClient: QueryClient): void {
  broadcastLaborOpenSessionChanged();
  void queryClient.invalidateQueries({ queryKey: ["hub", "labor", "active-session"] });
  void queryClient.invalidateQueries({ queryKey: ["hub", "labor", "open-session"] });
}

/** Clamp QC job surfaces also power list pages that query `["clamp-qc", "labor", "active-session", …]`; keep both in sync with the menu. */
export function invalidateClampQcLaborSessionQueries(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: ["clamp-qc", "labor", "active-session"] });
  invalidateIntegratorLaborMenuQueries(queryClient);
}
